import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import {
  geneDetailRoute,
  semiConfiguratorsRoute,
  searchResultsRoute,
} from '@src/routes'
import { Link } from '@src/components/Link'
import { Box, ButtonBase, Unstable_Grid2 as Grid } from '@mui/material'
import MUILink from '@mui/material/Link'
import { GeneFieldsFragment } from '@src/queries/GeneSearchQuery.generated'
import messages from '@src/routes/SearchResults/messages'
import GeneIcon from '@src/icons/GeneIcon'
import Truncate from '@src/components/Truncate'
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material'
import CaretRightIcon from '@src/icons/CaretRightIcon'
import { getValidSearch, SearchFocusType } from '@src/utils/searchUtils'

export const useStyles = makeStyles()((theme) => {
  return {
    root: {
      padding: theme.spacing(6, 0),
      borderBottom: `1px solid ${theme.palette.grey[600]}`,
    },
    title: {
      display: 'block',
      fontSize: theme.typography.pxToRem(24),
      fontWeight: theme.typography.fontWeightMedium,
      marginBottom: theme.spacing(1),
      color: theme.palette.primary.main,
    },
    geneWrapper: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      gap: theme.spacing(5),
      width: '100%',
    },
    speciesSectionWrapper: {
      display: 'flex',
      [theme.breakpoints.up('sm')]: {
        marginLeft: '130px',
      },
    },
    thumbnail: {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.grey[400]}`,
      borderRadius: 2,
      flexShrink: 0,
      height: '109px',
      width: '109px',
    },
    detail: {
      maxWidth: 'calc(100% - 109px)',
      flexBasis: 'calc(100% - 109px)',
      flexGrow: 1,
    },
    synonymsWrapper: {
      display: 'flex',
      flexDirection: 'row',
    },
    geneDataTitle: {
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(14),
    },
    geneDataRow: {
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(2),
      wordBreak: 'break-word',
      whiteSpace: 'nowrap',
    },
    geneDataRowExpanded: {
      whiteSpace: 'normal',
    },
    relatedProductsTitle: {
      fontWeight: theme.typography.fontWeightMedium,
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(3),
      marginTop: theme.spacing(4),
    },
    relatedProductsRow: {
      display: 'flex',
      flexFlow: 'row wrap',
      maxWidth: '770px',
      '& > *': {
        marginBottom: theme.spacing(1),
        width: '25%',
        paddingRight: theme.spacing(3),
      },
    },
    expandCta: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
    },
    relatedGenesLinkWrapper: {
      display: 'flex',
      marginTop: theme.spacing(5),
    },
    relatedGenesLink: {
      fontSize: theme.typography.pxToRem(16),
      '&:hover > *': {
        color: theme.palette.secondary.dark,
      },
    },
    caretRightIcon: {
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.primary.main,
      marginLeft: theme.spacing(3),
      [theme.breakpoints.up('md')]: {
        fontSize: theme.typography.pxToRem(10),
      },
    },
  }
})

const removeDupeSummary = (geneSummary: string | undefined | null) => {
  const summaryText = 'Summary: '
  let removedSummaryText
  if (geneSummary) {
    geneSummary.slice(0, 9) === summaryText
      ? (removedSummaryText = geneSummary.slice(9, geneSummary.length - 1))
      : (removedSummaryText = null)
  }
  return removedSummaryText
}
export interface GeneProps {
  gene: GeneFieldsFragment
  inProductSRP?: boolean
}

const Gene: React.FC<GeneProps> = ({ gene }) => {
  const { classes } = useStyles()
  const geneSummary = removeDupeSummary(gene.summary)
  const [showMore, setShowMore] = useState(false)

  const geneSpecies: string = gene.variants.reduce((acc, cur, i, arr) => {
    const delimiter = i === arr.length - 1 ? '' : ', '
    const out = `${cur.commonName} ${cur.symbol} (${cur.geneId})`
    return (acc += out + delimiter)
  }, '')

  const speciesWordCount: string[] = geneSpecies ? geneSpecies.split(' ') : []
  const summaryWordCount: string[] = geneSummary ? geneSummary.split(' ') : []
  return (
    <div className={classes.root} data-testid="gene-card">
      <div className={classes.geneWrapper}>
        <div className={classes.thumbnail}>
          <GeneIcon style={{ width: '100%', height: '100%' }} />
        </div>
        <div className={classes.detail}>
          <Link {...geneDetailRoute.index(gene.symbol)} passHref>
            <MUILink className={classes.title}>{gene.symbol}</MUILink>
          </Link>
          {gene.name && (
            <Box mb={4} fontSize={16}>
              {gene.name}
            </Box>
          )}

          {gene.synonyms.length > 0 && (
            <div className={classes.synonymsWrapper}>
              <Grid xs={3} md={2}>
                <div className={classes.geneDataTitle}>
                  <FormattedMessage {...messages.SYNONYMS}>
                    {(text) => <span>{text}:</span>}
                  </FormattedMessage>
                </div>
              </Grid>
              <Grid xs={9} md={10} data-testid="gene-synonyms">
                <div className={classes.geneDataRow}>
                  {gene.synonyms.join(', ')}
                </div>
              </Grid>
            </div>
          )}
        </div>
      </div>
      <div className={classes.speciesSectionWrapper}>
        <Grid container>
          {gene.variants.length > 0 && (
            <>
              <Grid xs={3} md={2}>
                <div className={classes.geneDataTitle}>
                  <FormattedMessage id="SPECIES" defaultMessage="Species">
                    {(text) => <span>{text}:</span>}
                  </FormattedMessage>
                </div>
              </Grid>
              <Grid xs={9} md={10} data-testid="gene-variants">
                <Box
                  lineHeight={1.7}
                  className={
                    showMore ? classes.geneDataRowExpanded : classes.geneDataRow
                  }
                >
                  <Truncate
                    allowShowMore={false}
                    wordCount={10}
                    showFullText={showMore}
                  >
                    {geneSpecies}
                  </Truncate>
                </Box>
              </Grid>
            </>
          )}
          {geneSummary && (
            <>
              <Grid xs={3} md={2}>
                <div className={classes.geneDataTitle}>
                  <FormattedMessage {...messages.SUMMARY}>
                    {(text) => <span>{text}:</span>}
                  </FormattedMessage>
                </div>
              </Grid>
              <Grid xs={9} md={10} data-testid="gene-summary">
                <Box
                  lineHeight={1.7}
                  className={
                    showMore ? classes.geneDataRowExpanded : classes.geneDataRow
                  }
                >
                  <Truncate
                    allowShowMore={false}
                    wordCount={10}
                    showFullText={showMore}
                  >
                    {geneSummary}
                  </Truncate>
                </Box>
              </Grid>
            </>
          )}
          {speciesWordCount.length - 1 > 10 ||
          summaryWordCount.length - 1 > 15 ? (
            <ButtonBase
              data-testid="gene-show-more"
              className={classes.expandCta}
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? (
                <>
                  <FormattedMessage {...messages.SHOW_LESS} />
                  <KeyboardArrowUp />
                </>
              ) : (
                <>
                  <FormattedMessage {...messages.SHOW_MORE} />
                  <KeyboardArrowDown />
                </>
              )}
            </ButtonBase>
          ) : null}
        </Grid>
      </div>
      {gene.relatedCategories.length > 0 && (
        <>
          <div className={classes.relatedProductsTitle}>
            <FormattedMessage {...messages.RELATED_PRODUCTS}>
              {(text) => <span>{text}:</span>}
            </FormattedMessage>
          </div>
          <div
            id="gene-categories"
            data-testid="gene-categories"
            className={classes.relatedProductsRow}
          >
            {gene.relatedCategories.map(
              (category) =>
                (category.name !== 'shRNA' && category.name !== 'siRNA' && (
                  <Link
                    key={category.name}
                    {...geneDetailRoute.index(gene.symbol, category.name)}
                    passHref
                  >
                    <MUILink color="primary">{category.name}</MUILink>
                  </Link>
                )) ||
                ((category.name === 'shRNA' || category.name === 'siRNA') && (
                  <>
                    <Link
                      key={category.name}
                      {...semiConfiguratorsRoute.index(
                        category.name,
                        gene.symbol
                      )}
                      passHref
                    >
                      <MUILink color="primary">{category.name}</MUILink>
                    </Link>
                  </>
                ))
            )}
          </div>
        </>
      )}
      {gene.name && (
        <div className={classes.relatedGenesLinkWrapper}>
          <Link
            href={`${searchResultsRoute.searchResults(
              gene.name
            )}${getValidSearch({
              term: gene.name,
              focus: SearchFocusType.Genes,
            })}`}
            passHref
          >
            <MUILink color="primary" className={classes.relatedGenesLink}>
              <FormattedMessage {...messages.SEE_ALL_RELATED_GENES} />
              <CaretRightIcon className={classes.caretRightIcon} />
            </MUILink>
          </Link>
        </div>
      )}
    </div>
  )
}

export default Gene
